import { useState, useEffect } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Tabs } from "antd";

import useLocale from "../../hooks/useLocale";
import { colors } from "../../utils/variables";
import { ComponentContainer, LayoutGrid } from "../LayoutContainers";
import OnlyText from "../OnlyText";
import PerformanceList from "../ListTemplates/PerformanceList";
import { fetchRankingByCode, fetchInstrumentsList } from "../../utils/fetchers";
import { __ } from "../../locales/translation";
import { instrumentNamesDictionary } from "../../locales/dictionaries/instruments";

export default function ProductTabs({ content }) {
    const locale = useLocale();
    const [tabsContent, setTabsContent] = useState(content);
    const [tabsContentPopulated, setTabsContentPopulated] = useState(false);
    const [tabs, setTabs] = useState([]);
    const { isLoading: isLoadingInstruments, data: instrumentsList } = useQuery({
        queryKey: ["instrumentsList"],
        queryFn: fetchInstrumentsList,
        select(data) {
            const list = {
                indices: {
                    descripcion: locale === "es" ? "Indices" : "Indices",
                    mostrar: false,
                },
            };
            for (const item of data) {
                list[item.codigo.toLowerCase()] = {
                    descripcion: item.descripcion[locale],
                    mostrar: item.mostrar,
                };
            }
            return list;
        },
    });

    useEffect(() => {
        if (!isLoadingInstruments && instrumentsList) {
            const filteredTabs = Object.values(content)
                .filter((item) => {
                    const instrument = instrumentsList[item.codigo.toLowerCase()];
                    return instrument && instrument.mostrar;
                })
                .map((item) => {
                    const labelEs = (item.titulo["es"]).toLowerCase();
                    return {
                        label: instrumentNamesDictionary[labelEs][locale] || labelEs,
                        key: item.codigo,
                        children: (
                            <PerformanceList
                                data={tabsContent[item.codigo]}
                                productType={item.codigo}
                                loading={!tabsContentPopulated || !tabsContent[item.codigo]}
                            />
                        ),
                    };
                });
    
            setTabs(filteredTabs);
        }
    }, [content, instrumentsList, locale, isLoadingInstruments, tabsContent]);
    

    // Fetch the rest of the contents
    const allContents = useQueries({
        queries: Object.values(content)
            .filter((item) => !item.rankings.length)
            .map((item) => {
                return {
                    queryKey: ["homeRankings", item.codigo],
                    queryFn: () => fetchRankingByCode(item.codigo),
                };
            }),
    });
    useEffect(() => {
        if (!tabsContentPopulated && allContents.every((fetch) => fetch.isFetched)) {
            setTabsContent((prev) => {
                const newContent = { ...prev };
                for (const item of allContents) {
                    newContent[item.data.codigo].rankings = item.data.rankings;
                }

                return newContent;
            });
            setTabsContentPopulated(true);
        }
    }, [tabsContent, allContents]);

    return (
        <ComponentContainer bgColor={colors.blackish_10}>
            <LayoutGrid spacingTop="8" spacingBottom="4" cols={1}>
                <OnlyText
                    title={__("Explorá rápidamente las inversiones y activos con mayor y menor rendimiento")}
                    paragraph={__(
                        "Seleccioná una inversión y en un vistazo observa las cotizaciones, títulos y empresas que con el mayor y menor rendimiento.",
                    )}
                    classNames="mb-4"
                />

                <Tabs className="cot-tabs" defaultActiveKey="1" items={tabs} />
            </LayoutGrid>
        </ComponentContainer>
    );
}
